import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Description as DescriptionIcon,
  VerifiedUser as VerifiedUserIcon,
  Assignment as AssignmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Science as ScienceIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  documentService,
  ndaService,
  certificateService,
} from "../services/api";

function Dashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, isInvestigador, isEstudiante, isStudent } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    documents: 0,
    ndas: 0,
    certificates: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);

  const sections = [];

  // Solo administradores pueden ver estas secciones
  if (isAdmin()) {
    sections.push(
      {
        title: "Certificados",
        description: "Administra tus certificados digitales",
        icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
        path: "/certificates",
        color: "success",
        count: stats.certificates,
      },
      {
        title: "NDAs",
        description: "Gestiona tus acuerdos de confidencialidad",
        icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
        path: "/ndas",
        color: "warning",
        count: stats.ndas,
      },
      {
        title: "Administración",
        description: "Panel de administración del sistema",
        icon: <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />,
        path: "/admin",
        color: "info",
        count: null,
      }
    );
  } else if (isInvestigador()) {
    // Investigadores pueden ver proyectos y NDAs
    sections.push(
      {
        title: "Mis Proyectos",
        description: "Gestiona tus proyectos de investigación",
        icon: <ScienceIcon sx={{ fontSize: 40 }} />,
        path: "/research-projects",
        color: "primary",
        count: null,
      },
      {
        title: "NDAs",
        description: "Gestiona tus acuerdos de confidencialidad",
        icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
        path: "/ndas",
        color: "warning",
        count: stats.ndas,
      }
    );
  } else if (isEstudiante() || isStudent()) {
    // Collaborators y estudiantes solo pueden ver sus proyectos y el buscador público
    sections.push(
      {
        title: "Mis Proyectos",
        description: "Ver tus proyectos de investigación",
        icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
        path: "/research-projects",
        color: "primary",
        count: null,
      },
      {
        title: "Buscador Público",
        description: "Explora proyectos de investigación disponibles",
        icon: <ScienceIcon sx={{ fontSize: 40 }} />,
        path: "/search",
        color: "info",
        count: null,
      }
    );
  }

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const promises = [];

      // Solo admins cargan todos los datos
      if (isAdmin()) {
        promises.push(
          documentService.getAll(),
          ndaService.getAll(),
          certificateService.getAll()
        );
      } else if (isInvestigador()) {
        // Investigadores solo cargan NDAs
        promises.push(
          Promise.resolve({ data: [] }), // documents
          ndaService.getAll(),
          Promise.resolve({ data: [] }) // certificates
        );
      } else {
        // Estudiantes no cargan datos de documentos, NDAs o certificados
        promises.push(
          Promise.resolve({ data: [] }), // documents
          Promise.resolve({ data: [] }), // ndas
          Promise.resolve({ data: [] }) // certificates
        );
      }

      const [documentsRes, ndasRes, certificatesRes] = await Promise.all(
        promises
      );

      const documents = documentsRes.data || [];
      const ndas = ndasRes.data || [];
      const certificates = certificatesRes.data || [];

      setStats({
        documents: documents.length,
        ndas: ndas.length,
        certificates: certificates.length,
      });

      const activity = [];

      // Solo agregar actividad según el rol
      if (isAdmin()) {
        activity.push(
          ...documents.slice(-3).map((doc) => ({
            type: "document",
            title: doc.title,
            date: doc.createdAt,
            status: doc.blockchainTxHash ? "verified" : "pending",
            icon: <DescriptionIcon />,
          })),
          ...ndas.slice(-3).map((nda) => ({
            type: "nda",
            title: nda.title,
            date: nda.createdAt,
            status: nda.status,
            icon: <AssignmentIcon />,
          })),
          ...certificates.slice(-3).map((cert) => ({
            type: "certificate",
            title: cert.title,
            date: cert.issueDate,
            status: cert.status,
            icon: <VerifiedUserIcon />,
          }))
        );
      } else if (isInvestigador()) {
        activity.push(
          ...ndas.slice(-3).map((nda) => ({
            type: "nda",
            title: nda.title,
            date: nda.createdAt,
            status: nda.status,
            icon: <AssignmentIcon />,
          }))
        );
      }

      const sortedActivity = activity
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);

      setRecentActivity(sortedActivity);
    } catch (err) {
      setError("Error al cargar datos del dashboard");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Welcome Section */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Bienvenido, {user?.name}
          </Typography>
          <Typography variant="body1" paragraph color="textSecondary">
            Sistema de gestión de firmas digitales con blockchain. Gestiona
            documentos, NDAs y certificados de forma segura y verificable.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {isAdmin() && (
            <>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h4" color="primary">
                          {stats.documents}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Documentos
                        </Typography>
                      </Box>
                      <DescriptionIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h4" color="warning.main">
                          {stats.ndas}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          NDAs
                        </Typography>
                      </Box>
                      <AssignmentIcon color="warning" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card>
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {stats.certificates}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Certificados
                        </Typography>
                      </Box>
                      <VerifiedUserIcon color="success" sx={{ fontSize: 40 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
          {isInvestigador() && (
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {stats.ndas}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        NDAs
                      </Typography>
                    </Box>
                    <AssignmentIcon color="warning" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={3}>
          {/* Main Sections */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {sections.map((section) => (
                <Grid item xs={12} sm={6} key={section.title}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Box sx={{ color: `${section.color}.main`, mr: 2 }}>
                          {section.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" component="h2">
                            {section.title}
                          </Typography>
                          {section.count !== null && (
                            <Chip
                              label={section.count}
                              color={section.color}
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {section.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        color={section.color}
                        onClick={() => navigate(section.path)}
                        variant="contained"
                        fullWidth
                      >
                        Ir a {section.title}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Actividad Reciente
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {recentActivity.length > 0 ? (
                <List dense>
                  {recentActivity.map((item, index) => (
                    <ListItem
                      key={index}
                      divider={index < recentActivity.length - 1}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap>
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {new Date(item.date).toLocaleDateString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No hay actividad reciente
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Acciones Rápidas
          </Typography>
          <Grid container spacing={2}>
            {isAdmin() && (
              <>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    onClick={() => navigate("/documents")}
                  >
                    Nuevo Documento
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<AssignmentIcon />}
                    onClick={() => navigate("/ndas")}
                  >
                    Crear NDA
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<VerifiedUserIcon />}
                    onClick={() => navigate("/certificates")}
                  >
                    Emitir Certificado
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<AdminPanelSettingsIcon />}
                    onClick={() => navigate("/admin")}
                  >
                    Administración
                  </Button>
                </Grid>
              </>
            )}
            {(isInvestigador() || isAdmin()) && (
              <>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<VerifiedUserIcon />}
                    onClick={() => navigate("/research-projects")}
                  >
                    Nuevo Proyecto
                  </Button>
                </Grid>
                {isInvestigador() && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<AssignmentIcon />}
                      onClick={() => navigate("/ndas")}
                    >
                      Crear NDA
                    </Button>
                  </Grid>
                )}
              </>
            )}
            {(isEstudiante() || isStudent()) && (
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<VerifiedUserIcon />}
                  onClick={() => navigate("/search")}
                >
                  Buscar Proyectos
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
}

export default Dashboard;
